import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { getBscScanUrl } from "@/lib/web3Client";
import { Segmented } from "@/components/paisa/ui";

const fmt = (v, d = 8) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: d });

const STATUS_TONE = {
  completed: "text-up",
  confirming: "text-primary",
  broadcasting: "text-primary",
  pending_admin: "text-warning",
  rejected: "text-down",
  cancelled: "text-muted-foreground",
};

const CAT_META = {
  deposit: { label: "Deposit", tone: "in" },
  withdraw: { label: "Withdraw", tone: "out" },
  transfer: { label: "Transfer", tone: "auto" },
  otc_trade: { label: "OTC trade", tone: "auto" },
  p2p_trade: { label: "P2P trade", tone: "auto" },
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "deposit", label: "Deposits" },
  { value: "withdraw", label: "Withdrawals" },
  { value: "transfer", label: "Transfers" },
  { value: "otc", label: "OTC" },
  { value: "p2p", label: "P2P" },
];

const GRID = "grid-cols-[1fr_1fr_1.2fr_1fr_1.4fr_110px_80px]";
const short = (s) => `${String(s).slice(0, 8)}…${String(s).slice(-6)}`;

export default function HistoryTable() {
  const [rows, setRows] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 15;

  const load = async () => {
    try {
      const params = { limit, skip };
      if (filterType !== "all") params.type = filterType;
      const r = await api.get("/api/wallet/movements", { params });
      setRows(r.data.movements || []);
      setTotal(r.data.total || 0);
    } catch {
      setRows((prev) => prev || []);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, skip]);

  const cancelWithdrawal = async (txId) => {
    if (!window.confirm("Cancel this withdrawal? Your balance will be refunded.")) return;
    try {
      await api.post(`/api/wallet/withdraw/${txId}/cancel`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div>
      <div className="sm:w-fit">
        <Segmented
          options={FILTERS}
          value={filterType}
          onChange={(v) => {
            setSkip(0);
            setFilterType(v);
          }}
          testIdPrefix="history-filter"
        />
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-surface/40">
        <div className="min-w-[860px]">
          <div className={`table-head grid ${GRID} gap-3 px-4 py-2.5`}>
            <span>Type</span>
            <span>Asset</span>
            <span className="text-right">Amount</span>
            <span>Status</span>
            <span>Details</span>
            <span className="text-right">Time</span>
            <span />
          </div>
          {rows === null ? (
            <p className="px-4 py-14 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-14 text-center text-sm text-muted-foreground">
              No {filterType === "all" ? "" : `${FILTERS.find((f) => f.value === filterType)?.label.toLowerCase()} `}transactions yet.
            </p>
          ) : (
            rows.map((m) => {
              const meta = CAT_META[m.category] || { label: m.category, tone: "auto" };
              const isOut = meta.tone === "out" || (meta.tone === "auto" && m.direction === "out");
              const trade = m.category === "p2p_trade" || m.category === "otc_trade";
              return (
                <div key={m.id} data-testid={`history-row-${m.id}`} className={`terminal-row ${GRID} gap-3 px-4 py-3 text-sm`}>
                  <span className="flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full ${isOut ? "bg-down" : "bg-up"}`} />
                    {meta.label}
                  </span>
                  <span>
                    {m.asset}
                    {m.network && m.network !== "internal" && <span className="ml-1 text-xs text-muted-foreground">· {m.network}</span>}
                    {trade && <span className="ml-1 text-xs text-muted-foreground">/ {m.fiat_asset}</span>}
                  </span>
                  <span className="num text-right">
                    <span className={isOut ? "text-down" : "text-up"}>
                      {isOut ? "−" : "+"}
                      {fmt(m.amount)}
                    </span>
                    {Number(m.fee || 0) > 0 && (
                      <span className="block text-[11px] text-muted-foreground">
                        fee {fmt(m.fee)}
                        {m.fee_asset ? ` ${m.fee_asset}` : ""}
                      </span>
                    )}
                    {trade && (
                      <span className="block text-[11px] text-muted-foreground">
                        {fmt(m.fiat_amount)} {m.fiat_asset} @ {fmt(m.price, 2)}
                      </span>
                    )}
                  </span>
                  <span className={`text-xs capitalize ${STATUS_TONE[m.status] || "text-muted-foreground"}`}>
                    {m.status === "confirming" || m.status === "broadcasting"
                      ? `${m.status} ${m.confirmations || 0}/${m.required_confirmations || 0}`
                      : String(m.status || "").replace("_", " ")}
                  </span>
                  <span className="num truncate text-xs text-muted-foreground">
                    {m.tx_hash &&
                      (m.network === "BEP20" ? (
                        <a href={getBscScanUrl(m.tx_hash)} target="_blank" rel="noopener noreferrer" title={m.tx_hash} className="inline-flex items-center gap-1 hover:text-primary">
                          {short(m.tx_hash)} <ExternalLink size={10} />
                        </a>
                      ) : (
                        <span title={m.tx_hash}>{short(m.tx_hash)}</span>
                      ))}
                    {m.to_address && !m.tx_hash && <span title={m.to_address}>→ {short(m.to_address)}</span>}
                    {m.counterparty_email && (
                      <span>
                        {m.direction === "out" ? "→ " : "← "}
                        {m.counterparty_email}
                      </span>
                    )}
                    {trade && <span className="capitalize">{m.side}</span>}
                  </span>
                  <span className="num text-right text-xs text-muted-foreground">
                    {new Date(m.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span className="text-right">
                    {m.category === "withdraw" && m.status === "pending_admin" && (
                      <button
                        onClick={() => cancelWithdrawal(m.id)}
                        data-testid={`history-cancel-${m.id}`}
                        className="rounded-md border border-border px-2.5 py-1 text-xs text-down transition hover:border-down"
                      >
                        Cancel
                      </button>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {total > limit && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="num text-muted-foreground">
            {skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSkip(Math.max(0, skip - limit))}
              disabled={skip === 0}
              data-testid="history-prev"
              className="rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-xs transition hover:border-primary disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setSkip(skip + limit)}
              disabled={skip + limit >= total}
              data-testid="history-next"
              className="rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-xs transition hover:border-primary disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
